import { Link, useParams } from "react-router-dom";
import Seo, { orgSchema, breadcrumbSchema } from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { FadeUp, ParallaxImage, Eyebrow } from "@/components/Reveal";
import { Accordion } from "@/components/Accordion";
import { getService, SERVICES, SITE } from "@/data/content";
import NotFound from "@/pages/NotFound";

export default function ServiceDetail() {
    const { slug } = useParams();
    const service = getService(slug);
    if (!service) return <NotFound />;

    const idx = SERVICES.findIndex((s) => s.slug === slug);
    const prev = SERVICES[(idx - 1 + SERVICES.length) % SERVICES.length];
    const next = SERVICES[(idx + 1) % SERVICES.length];

    return (
        <>
            <Seo
                title={`${service.name} — US & Canada | Dagaz Development`}
                description={service.answer.slice(0, 150)}
                path={`/services/${slug}`}
                image={service.image}
                jsonLd={[
                    orgSchema,
                    breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: service.name, path: `/services/${slug}` }]),
                    {
                        "@context": "https://schema.org",
                        "@type": "Service",
                        name: service.name,
                        provider: { "@type": "Organization", name: SITE.name, email: SITE.email },
                        areaServed: ["United States", "Canada"],
                        description: service.answer,
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        mainEntity: service.faqs.map((f) => ({
                            "@type": "Question",
                            name: f.q,
                            acceptedAnswer: { "@type": "Answer", text: f.a },
                        })),
                    },
                ]}
            />
            <PageHeader
                eyebrow={`Services — ${String(idx + 1).padStart(2, "0")}`}
                titleLines={[service.name]}
                answer={service.answer}
                crumbs={[{ name: "Services", path: "/services" }, { name: service.name }]}
            />

            <ParallaxImage
                src={service.image}
                alt={`${service.name} by Dagaz Development`}
                className="h-[46vh] md:h-[62vh] mx-6 md:mx-12 lg:mx-16"
                speed={9}
            />

            <section className="px-6 md:px-12 lg:px-16 py-24 md:py-36 bg-chalk grid lg:grid-cols-12 gap-14" data-testid="service-detail-body">
                <div className="lg:col-span-7">
                    <Eyebrow className="mb-8">What We Deliver</Eyebrow>
                    <ul className="border-t border-graphite/15">
                        {service.deliverables.map((d, i) => (
                            <FadeUp key={i} delay={0.05 * i}>
                                <li className="flex items-baseline gap-6 py-5 border-b border-graphite/15">
                                    <span className="font-sans text-[10px] font-medium tracking-[0.24em] text-gold shrink-0">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="font-serif font-light text-xl md:text-2xl text-graphite">{d}</span>
                                </li>
                            </FadeUp>
                        ))}
                    </ul>
                    <div className="mt-16">
                        <Eyebrow className="mb-8">Common Questions</Eyebrow>
                        <Accordion items={service.faqs} testidPrefix={`service-faq-${slug}`} />
                    </div>
                </div>
                <aside className="lg:col-span-4 lg:col-start-9">
                    <div className="lg:sticky lg:top-32 space-y-10">
                        <FadeUp className="border border-graphite/15 p-8 bg-fog/30">
                            <p className="font-sans text-[10px] font-medium tracking-[0.28em] uppercase text-slate mb-4">How the Budget Is Set</p>
                            <p className="font-sans text-sm leading-relaxed text-graphite/80">{service.budget}</p>
                        </FadeUp>
                        <FadeUp delay={0.1} className="border border-graphite/15 p-8 bg-fog/30">
                            <p className="font-sans text-[10px] font-medium tracking-[0.28em] uppercase text-slate mb-4">Typical Timeline</p>
                            <p className="font-sans text-sm leading-relaxed text-graphite/80">{service.timeline}</p>
                        </FadeUp>
                        <FadeUp delay={0.2} className="bg-forest bg-noise p-8">
                            <p className="font-serif font-light text-2xl text-chalk leading-snug">Discuss your {service.name.toLowerCase()} project.</p>
                            <Link
                                to="/start-a-project"
                                data-testid="service-cta-start-project"
                                className="group mt-6 inline-flex items-center gap-3 px-7 py-3.5 bg-chalk text-graphite font-sans text-[11px] font-semibold tracking-[0.22em] uppercase transition-colors duration-500 hover:bg-gold"
                            >
                                Start a Project
                                <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5" aria-hidden="true">→</span>
                            </Link>
                        </FadeUp>
                    </div>
                </aside>
            </section>

            <nav className="grid grid-cols-2 border-t border-graphite/15 bg-chalk" aria-label="More services" data-testid="service-prevnext">
                <Link to={`/services/${prev.slug}`} data-testid="service-prev" className="group px-6 md:px-12 py-10 border-r border-graphite/15 hover:bg-fog/40 transition-colors duration-500">
                    <span className="font-sans text-[10px] tracking-[0.24em] uppercase text-slate">← Previous</span>
                    <span className="block mt-2 font-serif font-light text-xl md:text-3xl text-graphite group-hover:italic transition-all duration-500">{prev.name}</span>
                </Link>
                <Link to={`/services/${next.slug}`} data-testid="service-next" className="group px-6 md:px-12 py-10 text-right hover:bg-fog/40 transition-colors duration-500">
                    <span className="font-sans text-[10px] tracking-[0.24em] uppercase text-slate">Next →</span>
                    <span className="block mt-2 font-serif font-light text-xl md:text-3xl text-graphite group-hover:italic transition-all duration-500">{next.name}</span>
                </Link>
            </nav>
        </>
    );
}
